import type { LeadKanbanCard } from '@troia-v3/types';
import type { CardAction } from '@/shared/components/ui';
import type { IconName } from '@/shared/icons';
import type { PopulatedLead } from '../types';
import type { LeadCardAction } from './LeadCard';

/**
 * Ações de lead no formato do `CardActionsMenu` shared (F36/F77) — fonte única
 * de verdade das ações do kebab de lead (kanban, grid e lista).
 *
 * Substitui o antigo `LeadActionsMenu` (specs próprias aposentadas): mesmas
 * ações/ordem do mockup (popRow), separadores via `dividerBefore` e
 * vermelho via `variant: 'danger'` (lose/delete).
 */

// Snooze ativo — checa alertLevel computado (kanban) ou alertSnoozedUntil no futuro
// (fallback p/ PopulatedLead, onde alertLevel é opcional).
function isSnoozeActive(lead: PopulatedLead | LeadKanbanCard): boolean {
  if (lead.alertLevel === 'snoozed') return true;
  if (lead.alertSnoozedUntil) {
    return new Date(lead.alertSnoozedUntil).getTime() > Date.now();
  }
  return false;
}

// Ações disponíveis baseadas no estado do lead.
// Ordem do mockup (popRow): ações neutras → grupo qualificação → grupo ganho/perdido
// → separador → Excluir (danger) SEMPRE por último.
function getAvailableActions(lead: PopulatedLead | LeadKanbanCard): LeadCardAction[] {
  const actions: LeadCardAction[] = ['view', 'edit', 'transfer'];

  actions.push(isSnoozeActive(lead) ? 'unsnooze' : 'snooze');

  if (lead.qualifyStatus !== 'qualified') actions.push('qualify');
  if (lead.qualifyStatus !== 'disqualified') actions.push('disqualify');
  if (lead.businessStatus !== 'won') actions.push('win');
  if (lead.businessStatus !== 'lost') actions.push('lose');

  actions.push('delete');

  return actions;
}

const ACTION_LABELS: Record<LeadCardAction, string> = {
  view: 'Ver Detalhes',
  edit: 'Editar',
  win: 'Marcar como ganho',
  lose: 'Marcar como perdido',
  transfer: 'Transferir lead',
  delete: 'Excluir',
  qualify: 'Qualificar',
  disqualify: 'Desqualificar',
  snooze: 'Silenciar alerta…',
  unsnooze: 'Reativar alertas',
};

const ACTION_ICONS: Record<LeadCardAction, IconName> = {
  view: 'eye',
  edit: 'edit',
  win: 'check-circle',
  lose: 'x-circle',
  transfer: 'user-plus',
  delete: 'trash',
  qualify: 'user-check',
  disqualify: 'x-circle',
  snooze: 'bell-off',
  unsnooze: 'bell',
};

// Ações destrutivas/negativas ficam vermelhas no menu (variant danger do DS)
const DANGER_ACTIONS = new Set<LeadCardAction>(['lose', 'delete']);

// Separadores do mockup: antes do 1º qualify/disqualify, antes do 1º win/lose,
// e antes do Excluir (que fica isolado no fim).
function needsSeparator(action: LeadCardAction, index: number, actions: LeadCardAction[]): boolean {
  const isQualifyGroup = action === 'qualify' || action === 'disqualify';
  const isFirstQualify = isQualifyGroup && (index === 0 || !['qualify', 'disqualify'].includes(actions[index - 1]));
  const isWinOrLose = action === 'win' || action === 'lose';
  const isFirstWinOrLose = isWinOrLose && (index === 0 || !['win', 'lose'].includes(actions[index - 1]));
  const isDelete = action === 'delete' && index > 0;
  return isFirstQualify || isFirstWinOrLose || isDelete;
}

/** Monta o array de `CardAction` do lead para o `CardActionsMenu` shared. */
export function buildLeadCardActions(
  lead: PopulatedLead | LeadKanbanCard,
  onAction: (action: LeadCardAction, lead: PopulatedLead | LeadKanbanCard) => void
): CardAction[] {
  const actions = getAvailableActions(lead);
  return actions.map((action, index) => ({
    label: ACTION_LABELS[action],
    icon: ACTION_ICONS[action],
    onClick: () => onAction(action, lead),
    variant: DANGER_ACTIONS.has(action) ? ('danger' as const) : ('default' as const),
    dividerBefore: needsSeparator(action, index, actions),
  }));
}
